/**
 * Interactive Content API Service
 * Handles surveys, polls, and quizzes for outbound communications
 */

import { apiClient } from '../apiClient';

// ============================================================================
// TYPES
// ============================================================================

export type ContentType = 'survey' | 'poll' | 'quiz';
export type ContentStatus = 'draft' | 'active' | 'paused' | 'archived';
export type QuestionType = 'multiple_choice' | 'single_choice' | 'text' | 'rating' | 'scale' | 'boolean' | 'ranking';

export interface Question {
  id: string;
  type: QuestionType;
  question: string;
  description?: string;
  required: boolean;
  options?: string[];
  correct_answer?: string | string[] | number | boolean;
  points?: number;
  order: number;
  conditional_logic?: {
    show_if?: {
      question_id: string;
      equals?: string | number | boolean;
      contains?: string;
      greater_than?: number;
      less_than?: number;
    };
  };
  min_value?: number;
  max_value?: number;
  min_label?: string;
  max_label?: string;
}

export interface ContentSettings {
  allow_anonymous?: boolean;
  require_all_questions?: boolean;
  show_progress_bar?: boolean;
  randomize_questions?: boolean;
  randomize_options?: boolean;
  one_response_per_member?: boolean;
  show_results_after_submit?: boolean;
  passing_score?: number;
  time_limit_minutes?: number;
  start_date?: string;
  end_date?: string;
  max_responses?: number;
  redirect_url?: string;
  thank_you_message?: string;
  branding?: {
    logo_url?: string;
    primary_color?: string;
    background_color?: string;
    show_org_name?: boolean;
  };
}

export interface ContentTheme {
  primary_color?: string;
  background_color?: string;
}

export interface InteractiveContent {
  id: number;
  uuid: string;
  name: string;
  description?: string;
  content_type: ContentType;
  status: ContentStatus;
  version: number;
  parent_id?: number;
  questions: Question[];
  settings: ContentSettings;
  theme?: ContentTheme;
  default_language: string;
  media_asset_id?: number;
  created_at: string;
  updated_at: string;
  published_at?: string;
  created_by_name?: string;
  question_count?: number;
  response_count?: number;
  link_count?: number;
  translations?: ContentTranslation[];
  links?: ContentLink[];
  stats?: {
    total_responses: number;
    completed_responses: number;
    avg_time_seconds?: number;
    avg_score?: number;
  };
}

export interface ContentTranslation {
  id: number;
  language_code: string;
  name?: string;
  description?: string;
  questions?: Question[];
  settings?: ContentSettings;
  is_ai_translated: boolean;
  human_verified: boolean;
  created_at: string;
}

export interface ContentLink {
  id: number;
  uuid: string;
  short_code: string;
  full_url: string;
  status: 'active' | 'disabled' | 'expired';
  expires_at?: string;
  max_responses?: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  campaign_id?: number;
  click_count?: number;
  response_count?: number;
  created_at: string;
}

export interface ContentResponse {
  id: number;
  uuid: string;
  member_id?: number;
  respondent_name?: string;
  respondent_email?: string;
  is_anonymous: boolean;
  answers: Record<string, unknown>;
  score?: number;
  passed?: boolean;
  status: 'in_progress' | 'completed' | 'abandoned';
  started_at: string;
  completed_at?: string;
  time_spent_seconds?: number;
  source_channel?: string;
  device_type?: string;
  browser?: string;
  os?: string;
  language_code: string;
}

export interface ContentAnalytics {
  summary: {
    total_responses: number;
    completed_responses: number;
    abandoned_responses: number;
    completion_rate: number;
    total_links: number;
    total_link_clicks: number;
    avg_time_spent_seconds?: number;
    avg_score?: number;
    passed_count?: number;
    first_response_at?: string;
    last_response_at?: string;
  };
  questions: {
    question_id: string;
    question_type: string;
    total_answers: number;
    unique_respondents: number;
    option_counts?: Record<string, number>;
    avg_rating?: number;
    correct_percentage?: number;
    avg_points?: number;
  }[];
  channels: {
    channel: string;
    count: number;
  }[];
  trend: {
    date: string;
    count: number;
  }[];
}

// ============================================================================
// REQUEST TYPES
// ============================================================================

export interface CreateContentRequest {
  content_type: ContentType;
  name: string;
  description?: string;
  questions: Question[];
  settings?: ContentSettings;
  branding?: ContentTheme;
  default_language?: string;
}

export interface UpdateContentRequest {
  name?: string;
  description?: string;
  questions?: Question[];
  settings?: ContentSettings;
  branding?: ContentTheme;
  status?: ContentStatus;
}

export interface CreateLinkRequest {
  expires_at?: string;
  max_responses?: number;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  campaign_id?: number;
  prefill_data?: Record<string, unknown>;
  custom_slug?: string;
}

export interface CreateTranslationRequest {
  language_code: string;
  name?: string;
  description?: string;
  questions?: Question[];
  thank_you_message?: string;
  is_ai_translated?: boolean;
}

export interface ListContentParams {
  content_type?: ContentType;
  status?: ContentStatus;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ListResponsesParams {
  status?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// API CLASS
// ============================================================================

class InteractiveContentAPI {
  private baseUrl = '/api/interactive-content';

  // ========================================
  // CONTENT CRUD
  // ========================================

  async list(params?: ListContentParams): Promise<{
    items: InteractiveContent[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await apiClient.get(this.baseUrl, { params });
    return response.data;
  }

  async get(id: number): Promise<InteractiveContent> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  async create(data: CreateContentRequest): Promise<InteractiveContent> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  async update(
    id: number,
    data: UpdateContentRequest,
    createNewVersion = false
  ): Promise<InteractiveContent> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data, {
      params: { create_new_version: createNewVersion },
    });
    return response.data;
  }

  async delete(id: number, hardDelete = false): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`, {
      params: { hard_delete: hardDelete },
    });
    return response.data;
  }

  async clone(id: number, newName?: string): Promise<InteractiveContent> {
    const response = await apiClient.post(`${this.baseUrl}/${id}/clone`, null, {
      params: newName ? { new_name: newName } : undefined,
    });
    return response.data;
  }

  async getVersions(id: number): Promise<InteractiveContent[]> {
    const response = await apiClient.get(`${this.baseUrl}/${id}/versions`);
    return response.data;
  }

  // ========================================
  // SHARING LINKS
  // ========================================

  async createLink(contentId: number, data: CreateLinkRequest): Promise<ContentLink> {
    const response = await apiClient.post(`${this.baseUrl}/${contentId}/links`, data);
    return response.data;
  }

  async listLinks(contentId: number): Promise<ContentLink[]> {
    const response = await apiClient.get(`${this.baseUrl}/${contentId}/links`);
    return response.data;
  }

  async deleteLink(linkId: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`${this.baseUrl}/links/${linkId}`);
    return response.data;
  }

  // ========================================
  // TRANSLATIONS
  // ========================================

  async addTranslation(
    contentId: number,
    data: CreateTranslationRequest
  ): Promise<ContentTranslation> {
    const response = await apiClient.post(`${this.baseUrl}/${contentId}/translations`, data);
    return response.data;
  }

  async getTranslations(contentId: number): Promise<ContentTranslation[]> {
    const response = await apiClient.get(`${this.baseUrl}/${contentId}/translations`);
    return response.data;
  }

  // ========================================
  // RESPONSES
  // ========================================

  async listResponses(
    contentId: number,
    params?: ListResponsesParams
  ): Promise<{
    responses: ContentResponse[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${contentId}/responses`, { params });
    return response.data;
  }

  async getResponse(contentId: number, responseId: number): Promise<ContentResponse> {
    const response = await apiClient.get(`${this.baseUrl}/${contentId}/responses/${responseId}`);
    return response.data;
  }

  // ========================================
  // ANALYTICS
  // ========================================

  async getAnalytics(contentId: number): Promise<ContentAnalytics> {
    const response = await apiClient.get(`${this.baseUrl}/${contentId}/analytics`);
    return response.data;
  }

  // ========================================
  // MEDIA LIBRARY INTEGRATION
  // ========================================

  async addToMediaLibrary(
    contentId: number,
    folderId?: number
  ): Promise<{
    message: string;
    media_asset_id: number;
    media_asset_uuid: string;
    short_url: string;
  }> {
    const response = await apiClient.post(`${this.baseUrl}/${contentId}/add-to-media-library`, null, {
      params: folderId ? { folder_id: folderId } : undefined,
    });
    return response.data;
  }
}

// ============================================================================
// PUBLIC API (No auth required - for embedded forms)
// ============================================================================

class InteractiveContentPublicAPI {
  private baseUrl = '/api/s';

  async getContent(shortCode: string, language?: string): Promise<{
    uuid: string;
    name: string;
    description?: string;
    content_type: ContentType;
    questions: Question[];
    settings: ContentSettings;
    theme?: ContentTheme;
    language: string;
    prefill_data?: Record<string, unknown>;
    link_id: number;
    content_id: number;
  }> {
    const response = await apiClient.get(`${this.baseUrl}/${shortCode}`, {
      params: language ? { lang: language } : undefined,
    });
    return response.data;
  }

  async submitResponse(
    shortCode: string,
    data: {
      answers: { question_id: string; answer: unknown }[];
      respondent_name?: string;
      respondent_email?: string;
      language_code?: string;
    },
    memberId?: number
  ): Promise<{
    success: boolean;
    response_id: number;
    response_uuid: string;
    thank_you_message: string;
    score?: number;
    passed?: boolean;
    correct_answers?: Record<string, unknown>;
    poll_results?: Record<string, Record<string, number>>;
    redirect_url?: string;
  }> {
    const response = await apiClient.post(`${this.baseUrl}/${shortCode}/respond`, data, {
      params: memberId ? { member_id: memberId } : undefined,
    });
    return response.data;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export const interactiveContentAPI = new InteractiveContentAPI();
export const interactiveContentPublicAPI = new InteractiveContentPublicAPI();

export default interactiveContentAPI;
